export interface UnitDefinition {
  dimension: string;
  factor: number;
}

export const unitDefinitions: Record<string, UnitDefinition> = {
  m: { dimension: 'length', factor: 1 }, cm: { dimension: 'length', factor: 0.01 }, mm: { dimension: 'length', factor: 0.001 },
  um: { dimension: 'length', factor: 1e-6 }, nm: { dimension: 'length', factor: 1e-9 }, km: { dimension: 'length', factor: 1000 },
  in: { dimension: 'length', factor: 0.0254 }, ft: { dimension: 'length', factor: 0.3048 }, mi: { dimension: 'length', factor: 1609.344 },
  kg: { dimension: 'mass', factor: 1 }, g: { dimension: 'mass', factor: 1e-3 }, mg: { dimension: 'mass', factor: 1e-6 },
  ug: { dimension: 'mass', factor: 1e-9 }, tonne: { dimension: 'mass', factor: 1000 }, lb: { dimension: 'mass', factor: 0.45359237 }, oz: { dimension: 'mass', factor: 0.028349523125 },
  s: { dimension: 'time', factor: 1 }, ms: { dimension: 'time', factor: 1e-3 }, us: { dimension: 'time', factor: 1e-6 },
  min: { dimension: 'time', factor: 60 }, h: { dimension: 'time', factor: 3600 }, day: { dimension: 'time', factor: 86400 },
  'm3': { dimension: 'volume', factor: 1 }, 'dm3': { dimension: 'volume', factor: 1e-3 }, 'cm3': { dimension: 'volume', factor: 1e-6 },
  L: { dimension: 'volume', factor: 1e-3 }, mL: { dimension: 'volume', factor: 1e-6 }, uL: { dimension: 'volume', factor: 1e-9 },
  'm/s': { dimension: 'speed', factor: 1 }, 'km/h': { dimension: 'speed', factor: 1 / 3.6 },
  'm/s2': { dimension: 'acceleration', factor: 1 }, 'cm/s2': { dimension: 'acceleration', factor: 0.01 },
  N: { dimension: 'force', factor: 1 }, J: { dimension: 'energy', factor: 1 }, W: { dimension: 'power', factor: 1 }, Pa: { dimension: 'pressure', factor: 1 },
  'kg/m3': { dimension: 'density', factor: 1 }, 'g/cm3': { dimension: 'density', factor: 1000 },
  deg: { dimension: 'angle', factor: Math.PI / 180 }, rad: { dimension: 'angle', factor: 1 },
};

export const siPrefixes: Record<string, number> = {
  p: 1e-12, n: 1e-9, u: 1e-6, m: 1e-3, c: 1e-2, d: 1e-1,
  '': 1, k: 1e3, M: 1e6, G: 1e9,
};

function trimFloatingPoint(value: number): number {
  return Number(value.toPrecision(15));
}

function temperatureToKelvin(value: number, unit: string): number {
  if (unit === 'K') return value;
  if (unit === 'degC') return value + 273.15;
  if (unit === 'degF') return ((value - 32) * 5) / 9 + 273.15;
  throw new RangeError(`Unknown temperature unit: ${unit}`);
}

function kelvinToTemperature(value: number, unit: string): number {
  if (unit === 'K') return value;
  if (unit === 'degC') return value - 273.15;
  if (unit === 'degF') return ((value - 273.15) * 9) / 5 + 32;
  throw new RangeError(`Unknown temperature unit: ${unit}`);
}

export function convertUnit(value: number, fromUnit: string, toUnit: string): number {
  if (!Number.isFinite(value)) throw new TypeError('Value must be finite.');
  const temperatureUnits = ['degC', 'K', 'degF'];
  if (temperatureUnits.includes(fromUnit) || temperatureUnits.includes(toUnit)) {
    if (!temperatureUnits.includes(fromUnit) || !temperatureUnits.includes(toUnit)) throw new RangeError('Temperature cannot be converted to a different physical dimension.');
    return trimFloatingPoint(kelvinToTemperature(temperatureToKelvin(value, fromUnit), toUnit));
  }
  const from = unitDefinitions[fromUnit];
  const to = unitDefinitions[toUnit];
  if (!from || !to) throw new RangeError(`Unknown unit: ${!from ? fromUnit : toUnit}`);
  if (from.dimension !== to.dimension) throw new RangeError(`Cannot convert ${from.dimension} to ${to.dimension}.`);
  return trimFloatingPoint((value * from.factor) / to.factor);
}

export function convertPrefix(value: number, fromPrefix: string, toPrefix: string): number {
  if (!Number.isFinite(value)) throw new TypeError('Value must be finite.');
  const from = siPrefixes[fromPrefix];
  const to = siPrefixes[toPrefix];
  if (from === undefined || to === undefined) throw new RangeError('Unknown SI prefix.');
  return trimFloatingPoint((value * from) / to);
}